import { useLocation } from 'react-router-dom'
import { UserButton } from '@clerk/clerk-react'
import { Menu } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAppStore } from '@/stores/app-store'
import { useMobileSheet } from '@/hooks/use-mobile'
import { NotificationsDrawer } from '@/features/notifications/notifications-drawer'
import { GlobalSearchTrigger } from '@/features/search/global-search'
import { CLERK_APPEARANCE } from './user-menu'

/**
 * Top bar — thin (56px) header with a subtle backdrop blur that softens
 * the transition into the page content below. We intentionally don't show
 * the page title here anymore since PageHeader lives inside each page and
 * carries a much richer hero treatment. The top bar is just chrome.
 */

const ROUTE_EYEBROWS: Record<string, string> = {
  '/': '// WORKSPACE',
  '/dashboard': '// WORKSPACE',
  '/leads': '// PIPELINE',
  '/leads/discover': '// PIPELINE / DISCOVER',
  '/campaigns': '// OUTREACH',
  '/content': '// CONTENT',
  '/inbox': '// CONVERSATIONS',
  '/settings': '// CONFIGURATION',
}

export function TopBar() {
  const location = useLocation()
  const { toggleSidebar } = useAppStore()
  const { setOpen: setMobileMenuOpen } = useMobileSheet()

  // Match any /section or /section/subpage to the right eyebrow
  const eyebrow =
    ROUTE_EYEBROWS[location.pathname] ??
    ROUTE_EYEBROWS[`/${location.pathname.split('/')[1] ?? ''}`] ??
    '// WORKSPACE'

  function handleMenuClick() {
    if (window.innerWidth < 768) {
      setMobileMenuOpen(true)
    } else {
      toggleSidebar()
    }
  }

  return (
    <header
      className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-[rgba(8,9,14,0.75)] px-4 backdrop-blur-md md:px-6"
    >
      {/* Menu button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={handleMenuClick}
        className="shrink-0 text-[var(--color-silver)] hover:bg-[rgba(255,255,255,0.04)] hover:text-[var(--color-silver-bright)]"
      >
        <Menu className="h-5 w-5" />
        <span className="sr-only">Toggle menu</span>
      </Button>

      {/* Mono eyebrow — tiny technical-authority hint instead of loud title */}
      <span className="eyebrow hidden md:inline-block">{eyebrow}</span>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Search — Cmd/Ctrl+K opens the command palette */}
      <GlobalSearchTrigger />

      {/* Notifications bell + drawer */}
      <NotificationsDrawer />

      {/* User button (mobile only — desktop user lives in sidebar) */}
      <div className="md:hidden">
        <UserButton afterSignOutUrl="/login" appearance={CLERK_APPEARANCE} />
      </div>
    </header>
  )
}
